import * as React from 'react';
import { AxiosError } from 'axios';
import { getText } from '../../helpers';
import Loader from '../_common/loader';
import { ErrorManager } from '../../utilities/error-manager';
import APIManager from '../../utilities/api-manager';

export default function KenshoLinkDemo(props: KenshoLinkDemoProps): JSX.Element {
    const { accessToken, onUnauthorizedResponse, onLoaded } = props;

    const [name, setName] = React.useState('');
    const [aliases, setAliases] = React.useState('');
    const [country, setCountry] = React.useState('');
    const [address, setAddress] = React.useState('');

    const [links, setLinks] = React.useState<Link[] | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<LinkError>({ hasError: false });

    function clearInputs(): void {
        setName('');
        setAliases('');
        setCountry('');
        setAddress('');
    }

    function populateSeqValues(seqNum: number, upToValue: string): void {
        clearInputs();
        const textKeyBase = `pages.solution.demos.kenshoLink.seqExample${seqNum}`;
        setName(getText(`${textKeyBase}.name`));
        if (upToValue === 'country' || upToValue === 'address' || upToValue === 'alias') setCountry(getText(`${textKeyBase}.country`));
        if (upToValue === 'address' || upToValue === 'alias') setAddress(getText(`${textKeyBase}.address`));
        if (upToValue === 'alias') setAliases(getText(`${textKeyBase}.alias`));
    }

    function handleBatchNameClick(value: string): void {
        clearInputs();
        setName(value);
    }

    function errorCallBack(reqError: AxiosError): void {
        let errorMessage = getText('pages.solution.demos.kenshoLink.generalError');
        if (reqError) {
            if (reqError.response && reqError.response.status === 403) onUnauthorizedResponse();
            else if (reqError.response && reqError.response.status === 429) errorMessage = getText('pages.solution.demos.kenshoLink.quotaHit');
            // Only log unexpected errors to avoid the red error notification toast on expected behavior
            else ErrorManager.apiError(reqError, { msg: errorMessage });
        }
        setIsLoading(false);
        setLinks(null);
        setError({ hasError: true, message: errorMessage });
    }

    function findMatches(): void {
        if (accessToken == null) return;
        /* eslint-disable @typescript-eslint/camelcase */
        setIsLoading(true);
        setError({ hasError: false });
        const payload = {
            records: [{ uid: '1', name: name || undefined, aliases: aliases.trim() === '' ? undefined : [aliases], country_iso3: country || undefined, address: address || undefined }],
            num_top_records: 1,
            include_response_fields: ['name']
        };
        APIManager.findKenshoLinkMatch(payload, accessToken, errorCallBack)
            .then(response => {
                if (response && response.status === 200) {
                    const record = response.data.records[0];
                    setLinks(record.links);
                    setIsLoading(false);
                    setError({ hasError: false });
                }
            });
        /* eslint-enable @typescript-eslint/camelcase */
    }

    let outputResults = null;
    if (links !== null && links.length === 0) { outputResults = <div>{getText('pages.solution.demos.kenshoLink.noResults')}</div>; }
    if (links !== null && links.length !== 0) {
        outputResults = (
            <div className="linking-demo__demo-table">
                <div className="linking-demo__table-title">{getText('pages.solution.demos.kenshoLink.outputTableTitle')}</div>
                <table className="linking-demo__table linking-demo__output-table">
                    <colgroup>
                        <col style={{ width: '33%' }} />
                        <col style={{ width: '33%' }} />
                        <col style={{ width: '33%' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>{getText('pages.solution.demos.kenshoLink.outputTableEntityName')}</th>
                            <th>{getText('pages.solution.demos.kenshoLink.outputTableMiId')}</th>
                            <th>{getText('pages.solution.demos.kenshoLink.outputTableConfidenceScore')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {links.map(link => (
                            <tr key={link.sp_company_id}>
                                <td>{link.name}</td>
                                <td>{link.sp_company_id}</td>
                                <td>{Math.round(link.link_score * 10000) / 100}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }
    if (links === null) { outputResults = <div>{getText('pages.solution.demos.kenshoLink.instructions')}</div>; }
    if (isLoading) { outputResults = <Loader minHeight="150px" />; }
    if (error.hasError) { outputResults = <div>{error.message}</div>; }

    React.useEffect(() => {
        if (onLoaded) onLoaded();
    }, []);

    return (
        <section className="linking-demo">
            <div>
                <h2 className="subheading subheading--with-border margin-top-m">
                    {getText('pages.solution.demos.kenshoLink.inputHeader')}
                </h2>
                <div>
                    <p>
                        {getText('pages.solution.demos.kenshoLink.intro')}
                    </p>
                </div>
                <div>
                    <div className="linking-demo__tutorial">
                        <div className="linking-demo__instructions">
                            <div>
                                {getText('pages.solution.demos.kenshoLink.batchExampleExplainer')}
                            </div>
                        </div>
                        <div className="linking-demo__examples linking-demo__batch-examples">
                            <div className="linking-demo__batch-example">
                                <span className="linking-demo__example-label">
                                    {getText('pages.solution.demos.kenshoLink.batchExample1')}
                                </span>
                                <div>
                                    <button className="linking-demo__example-link" type="button" onClick={() => handleBatchNameClick(getText('pages.solution.demos.kenshoLink.batchExample1'))}>
                                        {getText('pages.solution.demos.kenshoLink.populate')}
                                    </button>
                                </div>
                            </div>
                            <div className="linking-demo__batch-example">
                                <span className="linking-demo__example-label">
                                    {getText('pages.solution.demos.kenshoLink.batchExample2')}
                                </span>
                                <div>
                                    <button className="linking-demo__example-link" type="button" onClick={() => handleBatchNameClick(getText('pages.solution.demos.kenshoLink.batchExample2'))}>
                                        {getText('pages.solution.demos.kenshoLink.populate')}
                                    </button>
                                </div>
                            </div>
                            <div className="linking-demo__batch-example">
                                <span className="linking-demo__example-label">
                                    {getText('pages.solution.demos.kenshoLink.batchExample3')}
                                </span>
                                <div>
                                    <button className="linking-demo__example-link" type="button" onClick={() => handleBatchNameClick(getText('pages.solution.demos.kenshoLink.batchExample3'))}>
                                        {getText('pages.solution.demos.kenshoLink.populate')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="linking-demo__tutorial">
                    <div className="linking-demo__instructions padding-top-m">
                        <div>
                            {getText('pages.solution.demos.kenshoLink.seqExampleExplainer')}
                        </div>
                    </div>
                    <div className="linking-demo__examples margin-top-m">
                        <div className="linking-demo__seq-example">
                            <span className="linking-demo__example-label">
                                {getText('pages.solution.demos.kenshoLink.seqExample1.title')}
                            </span>
                            <div className="linking-demo__seq-example-links">
                                <button className="linking-demo__example-link" type="button" onClick={() => populateSeqValues(1, 'name')}>
                                    {getText('pages.solution.demos.kenshoLink.seqName')}
                                </button>
                                <button type="button" onClick={() => populateSeqValues(1, 'country')} className="linking-demo__example-link">
                                    +{getText('pages.solution.demos.kenshoLink.seqCountry')}
                                </button>
                                <button className="linking-demo__example-link" type="button" onClick={() => populateSeqValues(1, 'address')}>
                                    +{getText('pages.solution.demos.kenshoLink.seqAddress')}
                                </button>
                                <button className="linking-demo__example-link" type="button" onClick={() => populateSeqValues(1, 'alias')}>
                                    +{getText('pages.solution.demos.kenshoLink.seqAlias')}
                                </button>
                            </div>
                        </div>
                        <div className="linking-demo__seq-example">
                            <span className="linking-demo__example-label">
                                {getText('pages.solution.demos.kenshoLink.seqExample2.title')}
                            </span>
                            <div className="linking-demo__seq-example-links">
                                <button className="linking-demo__example-link" type="button" onClick={() => populateSeqValues(2, 'name')}>
                                    {getText('pages.solution.demos.kenshoLink.seqName')}
                                </button>
                                <button className="linking-demo__example-link" type="button" onClick={() => populateSeqValues(2, 'country')}>
                                    +{getText('pages.solution.demos.kenshoLink.seqCountry')}
                                </button>
                                <button className="linking-demo__example-link" type="button" onClick={() => populateSeqValues(2, 'address')}>
                                    +{getText('pages.solution.demos.kenshoLink.seqAddress')}
                                </button>
                                <button className="linking-demo__example-link" type="button" onClick={() => populateSeqValues(2, 'alias')}>
                                    +{getText('pages.solution.demos.kenshoLink.seqAlias')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="margin-top-xl linking-demo__demo-table">
                    <div className="linking-demo__table-title">
                        {getText('pages.solution.demos.kenshoLink.inputTableTitle')}
                    </div>
                    <table className="linking-demo__table">
                        <colgroup>
                            <col style={{ width: '25%' }} />
                            <col style={{ width: '20%' }} />
                            <col style={{ width: '15%' }} />
                            <col style={{ width: '40%' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>{getText('pages.solution.demos.kenshoLink.inputTableEntityName')}*</th>
                                <th>{getText('pages.solution.demos.kenshoLink.inputTableAliases')}</th>
                                <th>{getText('pages.solution.demos.kenshoLink.inputTableCountry')}</th>
                                <th>{getText('pages.solution.demos.kenshoLink.inputTableAddress')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <input placeholder={getText('pages.solution.demos.kenshoLink.placeholderName')} type="text" value={name} onChange={event => setName(event.target.value)} />
                                </td>
                                <td>
                                    <input placeholder={getText('pages.solution.demos.kenshoLink.placeholderAliases')} type="text" value={aliases} onChange={event => setAliases(event.target.value)} />
                                </td>
                                <td>
                                    <input placeholder={getText('pages.solution.demos.kenshoLink.placeholderCountry')} type="text" value={country} onChange={event => setCountry(event.target.value)} />
                                </td>
                                <td>
                                    <input placeholder={getText('pages.solution.demos.kenshoLink.placeholderAddress')} type="text" value={address} onChange={event => setAddress(event.target.value)} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="linking-demo__find-match-container">
                    <button className="button button--primary" type="button" onClick={findMatches} disabled={name === ''}>
                        {getText('pages.solution.demos.kenshoLink.findMatch')}
                    </button>
                </div>
            </div>
            <div className="margin-top-m">
                <div className="subheading subheading--with-border">{getText('pages.solution.demos.kenshoLink.outputHeader')}</div>
                <div className="margin-top-m">
                    {outputResults}
                </div>
            </div>
            <div className="margin-top-m">
                <span className="linking-demo__emphasis">{getText('pages.solution.demos.kenshoLink.demoNote')}</span>
                <span>
                    {getText('pages.solution.demos.kenshoLink.demoNoteText')}
                </span>
            </div>
        </section>
    );
}
